import { Editor } from "@tinymce/tinymce-react";
import { CheckCircle, Lock, Save } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

// Minimale Typdefinition für den TinyMCE Editor, um ohne externe Typen auszukommen
type TinyMCEEditorLike = {
	getBody?: () => HTMLElement;
	getContainer?: () => HTMLElement | null;
	theme?: { resizeTo?: (w: number | null, h: number) => void };
	on?: (event: string, handler: () => void) => void;
};

interface RaumEditorProps {
	inhalt: string;
	kannBearbeiten: boolean;
	ladeVorgang: boolean;
	speicherStatus: "bereit" | "speichert" | "gespeichert" | "fehler";
	onInhaltAendern: (content: string) => void;
	onSpeichern: (aktuellerInhalt: string) => void; // Korrigierte Signatur
	apiKey: string;
}

export function RaumEditor({
	inhalt,
	kannBearbeiten,
	ladeVorgang,
	speicherStatus,
	onInhaltAendern,
	onSpeichern,
	apiKey,
}: RaumEditorProps) {
	const editorRef = useRef<TinyMCEEditorLike | null>(null);
	const [localContent, setLocalContent] = useState(inhalt);
	const containerRef = useRef<HTMLDivElement>(null);
	// Mindesthöhe des Editors in Pixeln (TinyMCE erwartet Zahlenwerte)
	const [minEditorHeight, setMinEditorHeight] = useState<number>(500);
	const topInfoRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);
	const [editorReady, setEditorReady] = useState(false);

	// Synchronisiere externen Inhalt mit lokalem Zustand
	useEffect(() => {
		setLocalContent(inhalt);
	}, [inhalt]);

	// Hilfsfunktionen für exakte Höhenberechnung
	const getAvailableHeight = React.useCallback(() => {
		const container = containerRef.current;
		if (!container) {
			return 300;
		}
		const style = window.getComputedStyle(container);
		const paddingTop = Number.parseFloat(style.paddingTop || "0");
		const paddingBottom = Number.parseFloat(style.paddingBottom || "0");
		const innerHeight = container.clientHeight - paddingTop - paddingBottom;

		const topInfoVisible = !kannBearbeiten;
		const topInfoH = topInfoVisible
			? (topInfoRef.current?.offsetHeight ?? 0)
			: 0;
		const topInfoMargin = topInfoVisible ? 16 : 0; // mb-4

		const footerVisible = kannBearbeiten;
		const footerH = footerVisible ? (footerRef.current?.offsetHeight ?? 0) : 0;
		const footerMargin = footerVisible ? 12 : 0; // mt-3

		const available =
			innerHeight - topInfoH - topInfoMargin - footerH - footerMargin;
		return Math.max(available, 200);
	}, [kannBearbeiten]);

	const getEditorChromeHeight = React.useCallback(() => {
		const ed = editorRef.current;
		const container = ed?.getContainer?.();
		if (!container) {
			return 0;
		}
		const header = container.querySelector(
			".tox-editor-header",
		) as HTMLElement | null;
		const status = container.querySelector(
			".tox-statusbar",
		) as HTMLElement | null;
		const headerH = header?.offsetHeight ?? 0;
		const statusH = status?.offsetHeight ?? 0;
		return headerH + statusH;
	}, []);

	const updateEditorLayout = React.useCallback(() => {
		const available = getAvailableHeight();
		const chrome = getEditorChromeHeight();
		const contentHeight = Math.max(available - chrome, 100);
		setMinEditorHeight(contentHeight);
		const ed = editorRef.current;
		if (ed && typeof ed.theme?.resizeTo === "function") {
			try {
				ed.theme.resizeTo(null, contentHeight);
				const body = ed.getBody?.();
				if (body) body.style.minHeight = `${contentHeight}px`;
			} catch {
				/* noop */
			}
		}
	}, [getAvailableHeight, getEditorChromeHeight]);

	// Beobachte Größenänderungen und passe Editor exakt an
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}
		updateEditorLayout();
		const ro = new ResizeObserver(() => updateEditorLayout());
		ro.observe(containerRef.current);
		window.addEventListener("resize", updateEditorLayout);
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", updateEditorLayout);
		};
	}, [kannBearbeiten, editorReady, updateEditorLayout]);

	// Verzögert Änderungen an den übergeordneten State weitergeben
	useEffect(() => {
		const timerId = setTimeout(() => {
			if (kannBearbeiten && localContent !== inhalt) {
				onInhaltAendern(localContent);
			}
		}, 1000);

		return () => clearTimeout(timerId);
	}, [localContent, inhalt, kannBearbeiten, onInhaltAendern]);

	return (
		<div
			className="card min-h-[60vh] rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70"
			ref={containerRef}
		>
			{ladeVorgang ? (
				<div className="flex h-full items-center justify-center">
					<div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
				</div>
			) : (
				<>
					{/* Bearbeitungs-Info */}
					{!kannBearbeiten && (
						<div
							className="mb-4 rounded-lg border border-amber-100 bg-amber-50 p-3 text-amber-800 dark:border-amber-800/30 dark:bg-amber-900/30 dark:text-amber-300"
							ref={topInfoRef}
						>
							<p className="flex items-center">
								<Lock className="mr-2 h-5 w-5" />
								Du kannst diesen Text nur lesen. Nur der Besitzer kann den
								Inhalt bearbeiten.
							</p>
						</div>
					)}

					{/* WYSIWYG Editor */}
					<Editor
						apiKey={apiKey}
						disabled={!kannBearbeiten}
						init={{
							// Kombination aus fester Mindesthöhe und autoresize
							menubar: true,
							selection_restore: true,

							// Deutsche Sprachunterstützung
							language: "de",
							language_url:
								"https://cdn.tiny.cloud/1/no-api-key/tinymce/6/langs/de.js",

							// Erweiterte Plugin-Liste
							plugins: [
								// Basisformatierung
								"advlist",
								"autolink",
								"lists",
								"link",
								"image",
								"charmap",
								"preview",
								"anchor",
								"searchreplace",
								"visualblocks",
								"code",
								"fullscreen",
								"insertdatetime",
								"media",
								"table",
								"help",
								"wordcount",

								// Hinzugefügte technische Plugins
								"codesample",
								"visualchars",
								"quickbars",
								"hr",
								"nonbreaking",
								"emoticons",
								"print",
								"paste",
								"importcss",
								"directionality",
								"textpattern",
							],

							// Erweiterte Toolbar mit neuen Plugin-Buttons
							toolbar: [
								"undo redo | blocks | formatselect fontselect fontsizeselect |",
								"bold italic underline strikethrough | forecolor backcolor |",
								"alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent |",
								"link image media codesample table | charmap hr nonbreaking emoticons |",
								"removeformat | visualblocks code fullscreen print preview | help",
							].join(" "),

							// Feste Größe wird nach Init via theme.resizeTo gesetzt
							resize: false,

							// Tabellen-Toolbar
							table_toolbar:
								"tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",

							// CodeSample Sprachen
							codesample_languages: [
								{ text: "HTML/XML", value: "markup" },
								{ text: "JavaScript", value: "javascript" },
								{ text: "CSS", value: "css" },
								{ text: "PHP", value: "php" },
								{ text: "Python", value: "python" },
								{ text: "Java", value: "java" },
								{ text: "C", value: "c" },
								{ text: "C++", value: "cpp" },
								{ text: "JSON", value: "json" },
							],

							// Deutsche Übersetzungen für einige Standardtexte
							translations: {
								Redo: "Wiederholen",
								Undo: "Rückgängig",
								Cut: "Ausschneiden",
								Copy: "Kopieren",
								Paste: "Einfügen",
								"Select all": "Alles auswählen",
								"New document": "Neues Dokument",
								Ok: "OK",
								Cancel: "Abbrechen",
								Save: "Speichern",
								"Visual aids": "Visuelle Hilfen",
								Bold: "Fett",
								Italic: "Kursiv",
								Underline: "Unterstrichen",
								Strikethrough: "Durchgestrichen",
								Code: "Code",
								Paragraph: "Absatz",
								"Heading 1": "Überschrift 1",
								"Heading 2": "Überschrift 2",
								"Heading 3": "Überschrift 3",
								"Heading 4": "Überschrift 4",
								"Heading 5": "Überschrift 5",
								"Heading 6": "Überschrift 6",
							},

							// Custom CSS zum Verhindern des Schrumpfens
							content_style: `body { font-family:Helvetica,Arial,sans-serif; font-size:14px; min-height: ${minEditorHeight}px !important; }`,

							// Dark mode Unterstützung
							skin: document.body.classList.contains("dark")
								? "oxide-dark"
								: "oxide",
							content_css: document.body.classList.contains("dark")
								? "dark"
								: "default",

							// Callback: Mindesthöhe im Body absichern
							setup: (editor: TinyMCEEditorLike) => {
								editor.on?.("init", () => {
									const editorBody = editor.getBody?.();
									if (editorBody) {
										editorBody.style.minHeight = `${minEditorHeight}px`;
									}
									// Nach Init einmal exakte Höhe setzen
									setTimeout(() => updateEditorLayout(), 0);
								});
							},
						}}
						onEditorChange={(content: string) => {
							// Nur lokalen Zustand aktualisieren, nicht direkt den Parent
							if (kannBearbeiten) {
								setLocalContent(content);
							}
						}}
						onInit={(_evt: unknown, editor: TinyMCEEditorLike) => {
							editorRef.current = editor;
							setEditorReady(true);
						}}
						value={localContent}
					/>

					{/* Speicher-Status */}
					{kannBearbeiten && (
						<div
							className="mt-3 flex items-center justify-between"
							ref={footerRef}
						>
							<Button
								className="flex items-center gap-2"
								onClick={() => onSpeichern(localContent)}
								type="button" // Übergibt localContent
								variant="secondary"
							>
								<Save className="h-4 w-4" />
								Speichern
							</Button>

							<div className="text-sm">
								{speicherStatus === "speichert" && (
									<span className="flex items-center text-amber-600 dark:text-amber-400">
										<div className="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-amber-200 border-t-amber-500" />
										Speichert...
									</span>
								)}
								{speicherStatus === "gespeichert" && (
									<span className="flex items-center text-green-600 dark:text-green-400">
										<CheckCircle className="mr-1 h-4 w-4" /> Gespeichert
									</span>
								)}
								{speicherStatus === "fehler" && (
									<span className="text-red-600 dark:text-red-400">
										Fehler beim Speichern
									</span>
								)}
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
}
